import { useCallback, useEffect, useState } from 'react';
import { Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { useSettlementApi } from '@/services/api';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';
import type { ReceivableCustomer } from '@ip/shared-types';

interface ReceivablesCardProps {
  companyNip: string;
  /** Bump to re-fetch (e.g. after a payment was registered). */
  refreshKey?: number;
  /** Row click → filter the sales invoice list to this customer. */
  onCustomerClick: (customer: ReceivableCustomer) => void;
}

function formatDate(date: string | null): string {
  return date ? new Date(date).toLocaleDateString('pl-PL') : '-';
}

/**
 * Task A3: "Należności" — customers with open (outstanding > 0) sale
 * invoices, PLN-normalized, sorted by outstanding desc (backend order).
 * Tag-restricted users get a 403 from the aggregate endpoint — the card
 * hides itself in that case instead of erroring, mirroring how dashboards
 * degrade for restricted access.
 */
export function ReceivablesCard({ companyNip, refreshKey, onCustomerClick }: ReceivablesCardProps) {
  const [rows, setRows] = useState<ReceivableCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  const { getReceivables } = useSettlementApi();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getReceivables(companyNip);
      setRows(data);
      setUnavailable(false);
    } catch (error) {
      // Aggregate refused (tag-restricted user) or transient failure — hide
      // the card rather than blocking the whole sales view.
      console.error('Failed to load receivables:', error);
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  }, [companyNip, getReceivables]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (unavailable) return null;

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Wallet className="size-4" />
          <span>{t('sales.receivables.title')}</span>
        </CardTitle>
        <p className="text-muted-foreground text-xs">{t('sales.receivables.subtitle')}</p>
      </CardHeader>
      <CardContent className="px-4">
        {loading ? (
          <Skeleton className="h-16 w-full" />
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('sales.receivables.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-muted-foreground">{t('sales.receivables.customer')}</TableHead>
                  <TableHead className="text-muted-foreground text-right">{t('sales.receivables.invoiceCount')}</TableHead>
                  <TableHead className="text-muted-foreground text-right">{t('sales.receivables.outstanding')}</TableHead>
                  <TableHead className="text-muted-foreground text-right">{t('sales.receivables.overdue')}</TableHead>
                  <TableHead className="text-muted-foreground">{t('sales.receivables.oldestDueDate')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={`${row.customerNip ?? ''}:${row.customerName}`}
                    role="button"
                    tabIndex={0}
                    className="focus-visible:ring-ring cursor-pointer focus-visible:ring-2 focus-visible:outline-none"
                    title={t('sales.receivables.rowHint')}
                    onClick={() => onCustomerClick(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onCustomerClick(row);
                      }
                    }}
                  >
                    <TableCell className="py-2">
                      <p className="max-w-64 truncate text-sm font-medium" title={row.customerName}>
                        {row.customerName}
                      </p>
                      {row.customerNip && (
                        <p className="text-muted-foreground font-mono text-xs">NIP: {row.customerNip}</p>
                      )}
                    </TableCell>
                    <TableCell className="py-2 text-right tabular-nums">{row.invoiceCount}</TableCell>
                    <TableCell className="py-2 text-right font-mono text-sm font-semibold tabular-nums">
                      {formatPLN(row.outstandingPln)} PLN
                    </TableCell>
                    <TableCell className="py-2 text-right font-mono text-sm tabular-nums">
                      {row.overduePln > 0 ? (
                        <span className="text-rose-700 dark:text-rose-400">
                          {formatPLN(row.overduePln)} PLN
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="py-2 tabular-nums">{formatDate(row.oldestDueDate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
